/**
 * @fileoverview Molecule: a modal asking the user to confirm or back out.
 * @module components/molecules/ConfirmDialog
 */

import { Button } from "@/components/atoms"

import { Modal } from "../Modal/Modal"

import "./ConfirmDialog.css"

/** Props accepted by {@link ConfirmDialog}. */
export interface ConfirmDialogProps {
	/** Dialog heading. */
	readonly title: string
	/** What the user is being asked to confirm. */
	readonly message: string
	/** Label of the confirming action. Defaults to "Confirmer". */
	readonly confirmLabel?: string
	/** Label of the backing-out action. Defaults to "Annuler". */
	readonly cancelLabel?: string
	/** True to style the confirmation as destructive (e.g. a deletion). */
	readonly danger?: boolean
	/** Called when the user confirms. */
	readonly onConfirm: () => void
	/** Called when the user backs out, via Annuler, Escape or the overlay. */
	readonly onCancel: () => void
}

/**
 * Confirm/cancel modal, used before saving an edit or deleting a purchase.
 *
 * @param props - Copy, danger styling and the two outcomes.
 * @returns The rendered dialog.
 *
 * @example
 * <ConfirmDialog
 *   title="Supprimer Polo ?"
 *   message="Cette action est définitive."
 *   danger
 *   confirmLabel="Supprimer"
 *   onConfirm={remove}
 *   onCancel={close}
 * />
 */
export function ConfirmDialog({
	title,
	message,
	confirmLabel = "Confirmer",
	cancelLabel = "Annuler",
	danger = false,
	onConfirm,
	onCancel,
}: ConfirmDialogProps) {
	return (
		<Modal title={title} onClose={onCancel}>
			<p className="confirm-dialog__message">{message}</p>
			<div className="confirm-dialog__actions">
				<Button variant="ghost" onClick={onCancel}>
					{cancelLabel}
				</Button>
				<Button danger={danger} onClick={onConfirm}>
					{confirmLabel}
				</Button>
			</div>
		</Modal>
	)
}
